import type { SVGProps } from "react";

import type { TaskStatus } from "@/lib/tasks/queries";
import { cn } from "@/lib/utils";

// Inline SVGs that match the Figma status glyphs (node 505:5656). Keeping
// these local instead of pulling a new icon dep so the design can drift
// independently of any external icon set. 16px is the canonical size; the
// `size` prop scales the whole SVG.

const STATUS_COLORS: Record<TaskStatus, string> = {
  scheduled: "#02abff",
  todo: "#9b9b9b",
  in_progress: "#6366f1",
  needs_review: "#f59e0b",
  done: "#22c55e",
  canceled: "#9b9b9b",
  failed: "#d30505",
};

export const STATUS_LABELS: Record<TaskStatus, string> = {
  scheduled: "Scheduled",
  todo: "Todo",
  in_progress: "In progress",
  needs_review: "Needs review",
  done: "Done",
  canceled: "Canceled",
  failed: "Failed",
};

// Statuses the user can pick from the status menu. Scheduled / canceled /
// failed are intentionally excluded — scheduled is set by the runner from
// start_at; the others are reached via Cancel action / agent failure.
export const STATUS_PICKER_ORDER: TaskStatus[] = [
  "todo",
  "in_progress",
  "needs_review",
  "done",
];

type Props = SVGProps<SVGSVGElement> & {
  status: TaskStatus;
  size?: number;
};

export function TaskStatusIcon({ status, size = 16, className, ...rest }: Props) {
  const color = STATUS_COLORS[status];

  switch (status) {
    case "scheduled":
      // Dashed outline + small clock hands — a task waiting on its start_at.
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 16 16"
          fill="none"
          className={cn("shrink-0", className)}
          {...rest}
        >
          <circle
            cx="8"
            cy="8"
            r="6.25"
            stroke={color}
            strokeWidth="1.5"
            strokeDasharray="2 1.5"
          />
          <path
            d="M 8 4.75 L 8 8 L 10.25 9.25"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );

    case "todo":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 16 16"
          fill="none"
          className={cn("shrink-0", className)}
          {...rest}
        >
          <circle cx="8" cy="8" r="6.25" stroke={color} strokeWidth="1.5" />
        </svg>
      );

    case "in_progress":
      // Solid outline + bottom half filled. The fill arc is a single path
      // (move to left edge → arc to right edge along the bottom → close).
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 16 16"
          fill="none"
          className={cn("shrink-0", className)}
          {...rest}
        >
          <circle cx="8" cy="8" r="6.25" stroke={color} strokeWidth="1.5" />
          <path
            d="M 1.75 8 A 6.25 6.25 0 0 0 14.25 8 Z"
            fill={color}
          />
        </svg>
      );

    case "needs_review":
      // "Review" glyph from Figma — right half filled.
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 16 16"
          fill="none"
          className={cn("shrink-0", className)}
          {...rest}
        >
          <circle cx="8" cy="8" r="6.25" stroke={color} strokeWidth="1.5" />
          <path
            d="M 8 1.75 A 6.25 6.25 0 0 1 8 14.25 Z"
            fill={color}
          />
        </svg>
      );

    case "done":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 16 16"
          fill="none"
          className={cn("shrink-0", className)}
          {...rest}
        >
          <circle cx="8" cy="8" r="7" fill={color} />
          <path
            d="M 4.5 8.25 L 7 10.75 L 11.5 5.75"
            stroke="white"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );

    case "canceled":
      // Filled gray circle with a white X — matches the Figma "Canceled" glyph.
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 16 16"
          fill="none"
          className={cn("shrink-0", className)}
          {...rest}
        >
          <circle cx="8" cy="8" r="7" fill={color} />
          <path
            d="M 5.5 5.5 L 10.5 10.5 M 10.5 5.5 L 5.5 10.5"
            stroke="white"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      );

    case "failed":
      // Outlined red circle with a red exclamation — agent error signal.
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 16 16"
          fill="none"
          className={cn("shrink-0", className)}
          {...rest}
        >
          <circle cx="8" cy="8" r="6.25" stroke={color} strokeWidth="1.5" />
          <path
            d="M 8 4.5 L 8 8.75"
            stroke={color}
            strokeWidth="1.6"
            strokeLinecap="round"
          />
          <circle cx="8" cy="11" r="0.9" fill={color} />
        </svg>
      );
  }
}
